<div
  class="max-w-md mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 p-1 pt-8"
>
  <!-- Header -->
  <div class="text-center mb-8">
    <h2 class="text-2xl font-bold text-gray-800 mb-2">Assignment Settings</h2>
    <p class="text-gray-500">Configure the basic parameters for your assessment.</p>
  </div>

  <!-- Settings Card -->
  <div
    class="bg-white p-6 md:p-8 rounded-2xl shadow-lg shadow-gray-100 border border-gray-100 space-y-6"
  >
    <!-- Due Date Section -->
    <div>
      <label
        class="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase tracking-wide mb-1"
      >
        <lucide-icon [img]="icons.Calendar" class="w-3.5 h-3.5"></lucide-icon>
        Due Date
        <div class="relative">
          <button
            type="button"
            (click)="showDueDateInfo = !showDueDateInfo; $event.preventDefault()"
            class="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <lucide-icon [img]="icons.Info" class="w-3.5 h-3.5"></lucide-icon>
          </button>
          @if (showDueDateInfo) {
            <div
              class="fixed inset-0 z-40"
              (click)="showDueDateInfo = false"
            ></div>
            <div
              class="info-popover normal-case font-normal absolute z-50 left-0 top-full mt-1 w-72 bg-white rounded-xl shadow-xl shadow-gray-200/50 border border-gray-100 p-4"
            >
              <p class="text-sm text-gray-700 leading-relaxed">
                <span class="font-semibold text-gray-800">Due date</span>
                sets the deadline for students to complete the assignment. For
                example, selecting
                <span class="font-medium">"Tomorrow"</span> means students must
                finish by the end of tomorrow.
              </p>
              <p class="text-sm text-gray-500 mt-2 leading-relaxed">
                After the due date passes, students will no longer be able to
                start the assignment. Choose
                <span class="font-medium">"No due date"</span> if you want
                students to work at their own pace with no deadline.
              </p>
            </div>
          }
        </div>
      </label>
      <p class="text-xs text-gray-500 mb-3">
        Set a deadline for when students must complete the assignment.
      </p>

      <!-- Due Date Option Pills -->
      <div class="flex flex-wrap gap-2">
        @for (option of visibleDueDateOptions; track option.value) {
          <button
            type="button"
            (click)="selectDueDateOption(option)"
            class="px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 border cursor-pointer"
            [class.text-white]="isDueDateOptionSelected(option)"
            [class.shadow-md]="isDueDateOptionSelected(option)"
            [class.bg-white]="!isDueDateOptionSelected(option)"
            [class.text-gray-600]="!isDueDateOptionSelected(option)"
            [class.border-gray-200]="!isDueDateOptionSelected(option)"
            [class.hover:border-gray-300]="!isDueDateOptionSelected(option)"
            [class.hover:bg-gray-50]="!isDueDateOptionSelected(option)"
            [style.backgroundColor]="
              isDueDateOptionSelected(option) ? themeColor : ''
            "
            [style.borderColor]="
              isDueDateOptionSelected(option) ? themeColor : ''
            "
          >
            {{ option.label }}
          </button>
        }
        @if (hasHiddenDueDates || showMoreDueDates) {
          <button
            type="button"
            (click)="toggleShowMoreDueDates()"
            class="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600 cursor-pointer flex items-center gap-1"
          >
            @if (showMoreDueDates) {
              <span>Show less</span>
              <lucide-icon
                [img]="icons.ChevronUp"
                class="w-4 h-4"
              ></lucide-icon>
            } @else {
              <span>Show more</span>
              <lucide-icon
                [img]="icons.ChevronDown"
                class="w-4 h-4"
              ></lucide-icon>
            }
          </button>
        }
      </div>

      <!-- Custom Date Picker (shown when Custom is selected) -->
      @if (isCustomDueDate) {
        <div class="relative mt-3">
          <!-- Date Display Field -->
          <div
            class="w-full flex items-center pl-12 pr-4 py-3 border rounded-xl cursor-pointer transition-all duration-200"
            [class.border-gray-200]="!isCalendarOpen"
            [class.hover:border-gray-300]="!isCalendarOpen"
            [class.ring-2]="isCalendarOpen"
            [style.borderColor]="isCalendarOpen ? themeColor : ''"
            [style.--tw-ring-color]="themeColor + '33'"
            (click)="toggleCalendar()"
          >
            <div class="absolute left-4 top-1/2 -translate-y-1/2">
              <lucide-icon
                [img]="icons.Calendar"
                class="w-5 h-5 transition-colors duration-200"
                [style.color]="isCalendarOpen ? themeColor : '#9CA3AF'"
              ></lucide-icon>
            </div>
            <span
              class="flex-1"
              [class.text-gray-800]="!!dueDate"
              [class.text-gray-400]="!dueDate"
            >
              {{
                dueDate ? formatDateForDisplay(dueDate) : 'Select a due date'
              }}
            </span>
          </div>

          <!-- Calendar Picker -->
          @if (isCalendarOpen) {
            <app-calendar-picker
              [selectedDate]="dueDate"
              [themeColor]="themeColor"
              (dateSelected)="onCalendarDateSelected($event)"
              (closed)="onCalendarClosed()"
            />
          }
        </div>
      }
    </div>

    <!-- Time Limit Section -->
    <div>
      <label
        class="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase tracking-wide mb-1"
      >
        <lucide-icon [img]="icons.Clock" class="w-3.5 h-3.5"></lucide-icon>
        Time Limit
        <div class="relative">
          <button
            type="button"
            (click)="showTimeLimitInfo = !showTimeLimitInfo; $event.preventDefault()"
            class="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <lucide-icon [img]="icons.Info" class="w-3.5 h-3.5"></lucide-icon>
          </button>
          @if (showTimeLimitInfo) {
            <div
              class="fixed inset-0 z-40"
              (click)="showTimeLimitInfo = false"
            ></div>
            <div
              class="info-popover normal-case font-normal absolute z-50 left-0 top-full mt-1 w-72 bg-white rounded-xl shadow-xl shadow-gray-200/50 border border-gray-100 p-4"
            >
              <p class="text-sm text-gray-700 leading-relaxed">
                <span class="font-semibold text-gray-800">Time limit</span>
                sets how long a student has to complete the assignment once they
                start. A countdown timer will be shown during the quiz.
              </p>
              <p class="text-sm text-gray-500 mt-2 leading-relaxed">
                When time runs out, the assignment is automatically submitted
                and graded based on the answers provided. Choose
                <span class="font-medium">"No limit"</span> if you want
                students to take as long as they need.
              </p>
            </div>
          }
        </div>
      </label>
      <p class="text-xs text-gray-500 mb-3">
        Set how long students have to finish once they start the assignment.
      </p>
      <div class="flex flex-wrap gap-2">
        @for (option of visibleTimeLimitOptions; track option.value) {
          <button
            type="button"
            (click)="timeLimit = option.value"
            class="px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 border cursor-pointer"
            [class.text-white]="timeLimit === option.value"
            [class.shadow-md]="timeLimit === option.value"
            [class.bg-white]="timeLimit !== option.value"
            [class.text-gray-600]="timeLimit !== option.value"
            [class.border-gray-200]="timeLimit !== option.value"
            [class.hover:border-gray-300]="timeLimit !== option.value"
            [class.hover:bg-gray-50]="timeLimit !== option.value"
            [style.backgroundColor]="
              timeLimit === option.value ? themeColor : ''
            "
            [style.borderColor]="
              timeLimit === option.value ? themeColor : ''
            "
          >
            {{ option.label }}
          </button>
        }
        @if (hasHiddenTimeLimits) {
          <button
            type="button"
            (click)="toggleShowMoreTimeLimits()"
            class="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600 cursor-pointer flex items-center gap-1"
          >
            @if (showMoreTimeLimits) {
              <span>Show less</span>
              <lucide-icon
                [img]="icons.ChevronUp"
                class="w-4 h-4"
              ></lucide-icon>
            } @else {
              <span>Show more</span>
              <lucide-icon
                [img]="icons.ChevronDown"
                class="w-4 h-4"
              ></lucide-icon>
            }
          </button>
        }
      </div>
    </div>
  </div>

  <!-- Error Message -->
  @if (error$ | async; as error) {
    <div
      class="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
    >
      {{ error }}
    </div>
  }

  <!-- Spacer for fixed footer -->
  <div class="h-24"></div>
</div>

<!-- Fixed Footer -->
<div class="step-footer">
  <div class="max-w-md mx-auto flex gap-3 px-1">
    <button
      (click)="onBack()"
      class="flex items-center justify-center gap-2 px-6 py-3 text-gray-600 font-medium bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
    >
      <lucide-icon [img]="icons.ArrowLeft" class="w-4 h-4"></lucide-icon>
      Back
    </button>
    <button
      (click)="onSaveSettings()"
      [disabled]="(isCustomDueDate && !dueDate) || (isSaving$ | async)"
      class="flex-1 py-3 text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      [style.backgroundColor]="themeColor"
    >
      @if (isSaving$ | async) {
        <span class="flex items-center justify-center gap-2">
          <div
            class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"
          ></div>
          Saving...
        </span>
      } @else {
        Save Settings
      }
    </button>
  </div>
</div>
